import { Injectable } from '@angular/core';
import { TBleModeInitDTO, TConnectedDeviceInfoParams, TDevicesDB, TPairedDevice } from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import produce from 'immer';
import { from, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import { TInputAppState } from '../reducer';
import {
  AddBluetoothDevice,
  InitDevicesStore,
  RemoveBluetoothDevice,
  UpdateConnectedDeviceInfo,
  UpdateRfidScanMode,
} from './devices-db.actions';
import { getConnectedDeviceInfo, getPairedDeviceList, getRfidScanMode } from './devices-db.query';
import { TScanModes } from 'libs/services/src/lib/input-app/rfid-tag-reader.service';

@Injectable({
  providedIn: 'root',
})
export class DevicesStoreFacade {
  private initBleServiceSubcription = new Subject<TBleModeInitDTO>();
  constructor(private store: Store<TInputAppState>, private persistanceStorage: PersistentStorageService) {}

  getInitBleServiceSubscription() {
    return this.initBleServiceSubcription.asObservable();
  }

  emitInitBleServiceSubscription(initDTO: TBleModeInitDTO) {
    this.initBleServiceSubcription.next(initDTO);
  }

  initDevicesDB(initDTO: TBleModeInitDTO) {
    this.store.dispatch(new InitDevicesStore(initDTO));
  }

  getDevicesDBFromCache() {
    return from(this.persistanceStorage.get('[APP][INPUT_APP_DEVICES_DB]')).pipe(take(1));
  }

  updateDevicesDBInCache(devicesDB: TDevicesDB) {
    return from(this.persistanceStorage.set('[APP][INPUT_APP_DEVICES_DB]', devicesDB)).pipe(take(1));
  }

  addBluetoothDevice(devicesDB: TDevicesDB, deviceInfo: TPairedDevice) {
    const updatedDevicesDB = produce(devicesDB, (draft) => {
      const index = draft.pairedDeviceInfo.list.findIndex(
        (device) => device.device.deviceId === deviceInfo.device.deviceId,
      );
      if (index > -1) {
        draft.pairedDeviceInfo.list[index] = deviceInfo;
      } else draft.pairedDeviceInfo.list.push(deviceInfo);
    });
    return updatedDevicesDB;
  }

  removeDevice(devicesDB: TDevicesDB, deviceId: string) {
    const updatedDevicesDB = produce(devicesDB, (draft) => {
      const index = draft.pairedDeviceInfo.list.findIndex((device) => device.device.deviceId === deviceId);
      if (index !== -1) draft.pairedDeviceInfo.list.splice(index, 1);
    });
    return updatedDevicesDB;
  }

  removeAllDevices(devicesDB: TDevicesDB) {
    const updatedDevicesDB = produce(devicesDB, (draft) => {
      draft.pairedDeviceInfo.list = [];
    });
    return updatedDevicesDB;
  }

  getPairedDeviceList() {
    return this.store.pipe(select(getPairedDeviceList));
  }

  removeBluetoothDeviceAction(deviceId: string) {
    this.store.dispatch(new RemoveBluetoothDevice(deviceId));
  }

  addBluetoothDeviceAction(deviceInfo: TPairedDevice) {
    this.store.dispatch(new AddBluetoothDevice(deviceInfo));
  }

  getConnectedDeviceInfo() {
    return this.store.pipe(select(getConnectedDeviceInfo));
  }

  updateConnectedDeviceInfo(deviceInfo: TConnectedDeviceInfoParams) {
    this.store.dispatch(new UpdateConnectedDeviceInfo(deviceInfo));
  }

  updateRfidScanMode(newScanMode: TScanModes) {
    this.store.dispatch(new UpdateRfidScanMode(newScanMode));
  }

  getRfidScanMode() {
    return this.store.pipe(select(getRfidScanMode));
  }
}
